import { DatePicker, Divider } from "antd";
import { Dayjs } from "dayjs";
import { ReactNode, useMemo } from "react";
import { initVesselOfMainOp } from ".";
import Icon, { ICON_NAME } from "../../../../../../components/Icon";
import Menu from "../../../../../../components/Menu";
import { Vessel } from "../../../../../../components/Popup/PopupFilterVessel";
import { datetime } from "../../../../../../util/library/datetime";
import { selectTenantId } from "../../../../../../util/store/selectors";
import { getStore, useSelector } from "../../../../../../util/store/store";
import { ACTION_TYPE, OP_STATUS, OPERATION_CONFIRM_STATUS, OPERATION_TYPE, propContainer, SHORT_DATETIME_FORMAT } from "../../../../../../util/varibles/constants";
import { cloneObj, uuid } from "../../../../../../util/varibles/global";
import { IOperation, ISupportOp, IVessel } from "../../../../../../util/varibles/interface";
import { selectPlan } from "../../../../constants";
import { planValidation, VALIDATION } from "../../../../util/validation";
import { disabledDate } from "../../../Vessels/constants";
import { getEditOp, IVesselOfMain, setEditOp, useEditOp } from "../../EditOperationContext";
import AddChecklist, { ChecklistNames } from "../AddChecklist";
import AddNote from "../AddNote";
import CertainSelect from "../Certain/CertainSelect";
import styles from './style.module.scss';
import { getSupportOperation, IMainOp, setSupportOperation, useSupportOperation } from "./SupportOpContext";
import VesselFilter from "./VesselFilter";

const initSupportOp = (code: string, vessel: IVessel, start: number, finish: number): IOperation => {
    const tenantId = selectTenantId(getStore());
    const state = getStore();
    const operations = selectPlan.ops(state);
    const level = selectPlan.level(state);
    const newValue = {
        operation: {
            id: uuid(),
            operation_code: code,
            operation_type: OPERATION_TYPE.SUPPORT,
            status: OP_STATUS.NEW,
            tenant_id: tenantId,
            current_process: -1,
        } as ISupportOp,
        vessel,
        activity_log: [{ est_start_time: start }, { est_start_time: finish }],
        action_type: OPERATION_CONFIRM_STATUS.NEW
    } as IOperation
    const checkStatus = planValidation[VALIDATION.TIME](newValue, { operations, level });

    return { ...newValue, ...checkStatus }
}

const VesselSection: React.FC<{ mainOp: IMainOp['data'], vessel: IVessel, data: IVesselOfMain }> = ({ mainOp, vessel, data }) => {
    const { id: mainId, operation_code } = mainOp.operation;
    const { start, finish } = mainOp;
    const { id: vesselId } = vessel;
    const getSupport = getSupportOperation();
    const setSupport = setSupportOperation();
    const set = setEditOp();
    const { checked = false, ops = [] } = data || {};
    const [filter = {}] = useSupportOperation(state => state.mainOps[mainId]?.vesselsFilter?.[vesselId])

    const handleAdd = (ops: IOperation[]) => {
        const { count } = getSupport();
        setSupport({ count: count + 1 })
        set(({ supportOperations: prev }) => {
            const id = `${operation_code}-${count}`;

            return {
                supportOperations: {
                    ...prev, [mainId]: {
                        ...prev?.[mainId], [vesselId]: {
                            ...(prev?.[mainId]?.[vesselId] || {}), ops: [
                                ...ops,
                                initSupportOp(id, vessel, start, finish)
                            ]
                        }
                    }
                }
            }
        })
    }

    const handleChange = (index: number, value: IOperation) => {
        const temp = cloneObj(ops);
        const { action_type = OPERATION_CONFIRM_STATUS.UPDATE } = temp[index];
        temp[index] = { ...value, action_type };

        set(({ supportOperations: prev }) => ({
            supportOperations: {
                ...prev, [mainId]: {
                    ...prev?.[mainId], [vesselId]: {
                        ...(prev?.[mainId]?.[vesselId] || {}), ops: temp
                    }
                }
            }
        }))
    }

    const handleDelete = (index: number) => {
        const temp = cloneObj(ops);
        const { action_type } = temp[index];
        if (action_type === ACTION_TYPE.CREATE) {
            temp.splice(index, 1)
        } else {
            temp[index].is_delete = true
        }

        set(({ supportOperations: prev }) => ({
            supportOperations: {
                ...prev, [mainId]: {
                    ...prev?.[mainId], [vesselId]: {
                        ...(prev?.[mainId]?.[vesselId] || {}), ops: temp
                    }
                }
            }
        }))
    }

    const handleChecked = (checked: boolean) => {
        const { start, finish } = mainOp;
        set(({ supportOperations: prev }) => {
            const mainVessels = prev[mainId];
            if (mainVessels[vesselId]) {
                mainVessels[vesselId].checked = checked;
            } else {
                const { count } = getSupport();
                const id = `${operation_code}-${count}`;
                setSupport({ count: count + 1 });
                mainVessels[vesselId] = initVesselOfMainOp(vessel, [initSupportOp(id, vessel, start, finish)])
            }
            return { supportOperations: { ...prev, [mainId]: mainVessels } }
        })
    }

    return <div className={styles.vesselSection} data-selected={checked} data-show={Object.keys(filter).every(key => filter[key])}>
        <div className='pr-[50px]'>
            <Vessel data={vessel} checked={checked} width="max-content" onChange={handleChecked} />
            <Menu
                className={styles.vesselMenu}
                placement="bottomRight"
                items={[{
                    key: 'add',
                    onClick: () => handleAdd(ops),
                    label: <div className='menu-line'>
                        <Icon icon={ICON_NAME.ADD_2} />
                        <span>Add trip</span>
                    </div>
                }]}
            />
        </div>
        <div data-lev='ops' data-visible={checked}>
            {ops.map((item, i) => !item.is_delete && <VesselOperation key={item.operation.id} data={item} onChange={value => handleChange(i, value)} onDelete={() => handleDelete(i)} />)}
        </div>
    </div>
}

const VesselOperation: React.FC<{ data: IOperation, onChange(value: IOperation): void, onDelete(): void }> = ({ data, onChange, onDelete }) => {
    const [start, finish] = data.activity_log;
    const { note = "", operation_code, checklist_ids = [] } = data.operation;

    const handleChangeTime = (time: [start: Dayjs | null, end: Dayjs | null]) => {
        const [newStart = 0, newFinish = 0] = time || [];
        const temp = cloneObj(data.activity_log);
        temp[0].est_start_time = newStart ? datetime(newStart).time : 0;
        temp[1].est_start_time = newFinish ? datetime(newFinish).time : 0;
        const newValue = { ...data, activity_log: temp };
        const state = getStore();
        const operations = selectPlan.ops(state);
        const level = selectPlan.level(state)
        const checkStatus = planValidation[VALIDATION.TIME](newValue, { operations, level });
        onChange({ ...newValue, ...checkStatus })
    }

    return <div data-lev="op">
        <div className="flex">
            <div className="font-[600] min-w-[90px] leading-[32px]">
                Trip: #{data.operation.operation_code}
            </div>
            <Divider className="h-[90%] my-auto" orientation="vertical" />
        </div>
        <div className="flex flex-col gap-1">
            <div className="flex">
                <div className="flex items-center h-[36px]">
                    <div className="flex items-center gap-1 mr-5 min-w-[310px] w-[310px]">
                        <div className="whitespace-nowrap">In duration:</div>
                        <DatePicker.RangePicker value={[datetime(start.est_start_time).value, datetime(finish.est_start_time).value]} {...{
                            ...propContainer,
                            className: 'w-full h-[32px] rounded-[32px]',
                            allowClear: false,
                            showTime: { format: 'HH:mm', minuteStep: 15 },
                            disabledDate,
                            format: SHORT_DATETIME_FORMAT,
                            placeholder: ['Start time', 'Finish time'],
                            onChange: handleChangeTime,
                        }} />
                    </div>
                    <CertainSelect value={data.operation.certain_mode} onChange={certain_mode => onChange({ ...data, operation: { ...data.operation, certain_mode } })} />
                </div>
                <div className="ml-[8px] flex items-center">
                    <AddNote
                        isBubble={false}
                        value={note}
                        title={`#${operation_code}`}
                        onChange={value => onChange({ ...data, operation: { ...data.operation, note: value } })}
                    />
                </div>
                <AddChecklist
                    isContent={false}
                    data={data.operation}
                    onChange={value => onChange({ ...data, operation: { ...data.operation, checklist_ids: value } })}
                />
                <div className="bt-ghost" data-lev='delete' data-icon='true' data-danger='true' onClick={onDelete}>
                    <Icon icon={ICON_NAME.DELETE} danger />
                </div>
            </div>
            {note.length > 0 && <AddNote
                isBubble={false}
                isLabel={false}
                isContent
                value={note}
                title={`#${operation_code}`}
                onChange={value => onChange({ ...data, operation: { ...data.operation, note: value } })}
            />}
            {checklist_ids.length > 0 && <div className="flex flex-wrap">
                <ChecklistNames
                    checklist_ids={checklist_ids}
                    onClear={id => onChange({
                        ...data,
                        operation: {
                            ...data.operation,
                            checklist_ids: checklist_ids.filter(item => item !== id)
                        }
                    })}
                />
            </div>}
        </div>
    </div>
}

interface IProps {
    mainOp: IMainOp['data']
}

const Vessels: React.FC<IProps> = ({ mainOp }) => {
    const { id: mainId } = mainOp.operation;
    const vessels = useSelector(selectPlan.vessels);
    const getOp = getEditOp();
    const excludeVesselId = getOp().vessel?.id;
    const [supportOperations] = useEditOp(state => state.supportOperations);
    ;

    const { top, normal } = useMemo(() => {
        const top: ReactNode[] = [], normal: ReactNode[] = [];
        vessels.forEach(vessel => {
            if (vessel.id !== excludeVesselId) {
                const { checked } = supportOperations[mainId]?.[vessel.id] || {};
                const Node = <VesselSection
                    key={vessel.id}
                    mainOp={mainOp}
                    data={supportOperations[mainId]?.[vessel.id]}
                    vessel={vessel}
                />;
                if (checked)
                    top.push(Node)
                else
                    normal.push(Node)
            }
        });
        return { top, normal }
    }, [vessels, excludeVesselId, supportOperations, mainId, mainOp])

    return <div className="mt-3 relative h-full">
        <div className='flex justify-between gap-2 items-center h-[32px] mb-[2px]'>
            <div className="text-[15px]">Available Vessels:</div>
        </div>
        <VesselFilter mainOp={mainOp} />
        <div className={styles.vessels}>
            <div className="flex flex-col gap-[2px] ">
                {top}
                {normal}
            </div>
        </div>
    </div>
}

export default Vessels;
